import { useRef, useState } from 'react';

import { createBtnFromDefinition } from './use-buttons-layer';
import { getTrafficLayerPreference, setTrafficLayerPreference } from '../utils/local-storage-utils';

import styles from '../bringg-google-map-provider.module.scss';

type UseTrafficLayerProps = {
	map: google.maps.Map | null;
	allowTrafficLayer: boolean;
	trafficButtonTranslation?: string;
	localStorageKey?: string;
};

export const useTrafficLayer = ({
	map,
	allowTrafficLayer,
	trafficButtonTranslation,
	localStorageKey
}: UseTrafficLayerProps) => {
	const [trafficLayerInstance, setTrafficLayerInstance] = useState<google.maps.TrafficLayer | null>(null);
	const initialTrafficEnabled = localStorageKey ? getTrafficLayerPreference(localStorageKey) ?? false : false;
	const [trafficLayerEnabled, setTrafficLayerEnabled] = useState(initialTrafficEnabled);
	const buttonIndexRef = useRef<number | null>(null);
	const controlPosition = google.maps.ControlPosition.BOTTOM_CENTER;

	const renderButton = () => {
		let instanceToUse = trafficLayerInstance;
		if (!trafficLayerInstance) {
			instanceToUse = new google.maps.TrafficLayer();
			setTrafficLayerInstance(instanceToUse);
		}

		// Apply traffic layer immediately if it should be enabled and isn't already applied
		if (trafficLayerEnabled && map && instanceToUse && !instanceToUse.getMap()) {
			instanceToUse.setMap(map);
		}

		const controlButton = createBtnFromDefinition({
			text: trafficButtonTranslation || 'Traffic',
			className: styles.trafficButton,
			clickHandler: e => {
				e.preventDefault();
				setTrafficLayerEnabled(prev => {
					const newValue = !prev;
					if (localStorageKey) {
						setTrafficLayerPreference(localStorageKey, newValue);
					}
					return newValue;
				});
			},
			'data-test-id': 'traffic-btn'
		});

		map?.controls[controlPosition].push(controlButton);

		buttonIndexRef.current = 1;
	};

	const removeAll = () => {
		if (trafficLayerInstance) {
			trafficLayerInstance?.setMap(null);
			setTrafficLayerInstance(null);
		}
		if (buttonIndexRef?.current !== null) {
			buttonIndexRef.current = null;
			map?.controls[controlPosition]?.clear();
		}
	};

	if (!map || !allowTrafficLayer) {
		removeAll();
		return;
	}

	if (buttonIndexRef?.current === null) {
		renderButton();
		return;
	}

	if (!!trafficLayerInstance?.getMap() !== trafficLayerEnabled) {
		trafficLayerInstance?.setMap(trafficLayerEnabled ? map : null);
	}
};
